import {
  decodedValueEqual,
  decodeTaggedProps,
  editableScalarKind,
  type DecodedValue,
  type MergeConflictAgentConflict,
  type MergeConflictAgentField,
  type MergeConflicts,
  type TaggedNode,
  type TaggedRel
} from '@axilon-apps/domains-ontology-ui-state'
import { renderValue, renderValueDisambiguated } from './entity-display'
import { conflictSpec } from './BranchConflictView'
import { entityDisplayName, relLabel, urnDisplayName } from './entity-display'

/**
 * Digest the live conflict set into the shape the merge-conflict agent reads —
 * each conflict's differing properties with both sides' values rendered, plus
 * which are editable scalars and whether the conflict is structural. The page
 * owns decoding the type-tagged props here so the api-service stays codec-free.
 *
 * Relationship logical ids use a non-printable unit-separator between
 * start/type/end, which the model cannot reproduce; the digest exposes a
 * printable `id` for the agent and carries the real `logicalId` so a pick still
 * applies to the right conflict.
 */

const REL_ID_SEPARATOR = '\u001f'

type DecodedSide = Record<string, DecodedValue> | null
type ConflictEntity = TaggedNode | TaggedRel | null

function decodeSide(props: Record<string, unknown> | undefined | null): DecodedSide {
  if (props === undefined || props === null) return null
  try {
    return decodeTaggedProps(props)
  } catch {
    return null
  }
}

function renderAt(side: DecodedSide, key: string): string | undefined {
  if (side && key in side) return renderValue(side[key])
  return undefined
}

function fieldDigest(
  name: string,
  base: DecodedSide,
  ours: DecodedSide,
  theirs: DecodedSide
): MergeConflictAgentField {
  const decoded =
    ours && name in ours ? ours[name] : theirs && name in theirs ? theirs[name] : undefined
  const field: MergeConflictAgentField = {
    name,
    editable: decoded !== undefined && editableScalarKind(decoded) !== null
  }
  const base_ = renderAt(base, name)
  let target = renderAt(ours, name)
  let source = renderAt(theirs, name)
  // A conflicting field whose sides render identically is a type-only
  // difference — spell the kinds out so the agent can tell them apart.
  if (target !== undefined && source !== undefined && target === source) {
    target = ours && name in ours ? renderValueDisambiguated(ours[name]) : target
    source = theirs && name in theirs ? renderValueDisambiguated(theirs[name]) : source
  }
  if (base_ !== undefined) field.base = base_
  if (target !== undefined) field.target = target
  if (source !== undefined) field.source = source
  return field
}

function labelsOf(entity: ConflictEntity): string[] {
  return entity && 'labels' in entity && Array.isArray(entity.labels) ? entity.labels : []
}

/**
 * A short, human description of what one side did to an entity relative to the
 * merge base — for structural conflicts, where there are no per-field rows to
 * show. "removed" when the side dropped it; otherwise the label + property
 * changes it made (capped so the prompt stays readable).
 */
function describeSide(base: ConflictEntity, side: ConflictEntity): string {
  if (side === null) return 'removed this entity'
  const baseProps = decodeSide(base?.properties) ?? {}
  const sideProps = decodeSide(side.properties) ?? {}
  const parts: string[] = []

  const baseLabels = labelsOf(base)
  const sideLabels = labelsOf(side)
  for (const l of sideLabels.filter((x) => !baseLabels.includes(x))) parts.push(`added label ${l}`)
  for (const l of baseLabels.filter((x) => !sideLabels.includes(x)))
    parts.push(`removed label ${l}`)

  const keys = [...new Set([...Object.keys(baseProps), ...Object.keys(sideProps)])].sort()
  for (const k of keys) {
    if (k === 'id') continue
    const inBase = k in baseProps
    const inSide = k in sideProps
    // Structural comparison — rendered text masks type-only changes.
    if (inBase && inSide && decodedValueEqual(baseProps[k], sideProps[k])) continue
    if (!inBase && !inSide) continue
    const b = inBase ? renderValue(baseProps[k]) : undefined
    const s = inSide ? renderValue(sideProps[k]) : undefined
    if (b === undefined) parts.push(`set ${k}=${s}`)
    else if (s === undefined) parts.push(`cleared ${k}`)
    else if (b === s) {
      // Type-only change: spell the kinds out.
      parts.push(
        `${k} ${renderValueDisambiguated(baseProps[k])} → ${renderValueDisambiguated(sideProps[k])}`
      )
    } else parts.push(`${k} ${b} → ${s}`)
  }

  if (parts.length === 0) return base === null ? 'added this entity' : 'kept unchanged'
  return parts.slice(0, 6).join('; ')
}

function relConflictName(rel: TaggedRel | null | undefined): string | undefined {
  return rel ? relLabel(rel) : undefined
}

export function buildAgentConflicts(conflicts: MergeConflicts): MergeConflictAgentConflict[] {
  const out: MergeConflictAgentConflict[] = []

  for (const c of conflicts.nodes) {
    const spec = conflictSpec(c.ours, c.theirs, c.fields)
    const base = decodeSide(c.base?.properties)
    const ours = decodeSide(c.ours?.properties)
    const theirs = decodeSide(c.theirs?.properties)
    const entry: MergeConflictAgentConflict = {
      id: c.id,
      logicalId: c.id,
      entityKind: 'node',
      label: c.ours
        ? entityDisplayName(c.ours)
        : c.theirs
          ? entityDisplayName(c.theirs)
          : urnDisplayName(c.id),
      structural: spec.wholeEntityOnly,
      fields: spec.fields.map((name) => fieldDigest(name, base, ours, theirs))
    }
    // An add/add collision — both branches newly added this node (no base) — can
    // be split into two nodes instead of merged.
    if (c.base === null && c.ours !== null && c.theirs !== null) entry.addAdd = true
    if (entry.structural) {
      entry.targetChange = describeSide(c.base, c.ours)
      entry.sourceChange = describeSide(c.base, c.theirs)
    }
    out.push(entry)
  }

  for (const c of conflicts.rels) {
    const spec = conflictSpec(c.ours, c.theirs, c.fields)
    const base = decodeSide(c.base?.properties)
    const ours = decodeSide(c.ours?.properties)
    const theirs = decodeSide(c.theirs?.properties)
    const entry: MergeConflictAgentConflict = {
      // Printable id for the agent (real id has a non-printable separator).
      id: c.id.split(REL_ID_SEPARATOR).join('|'),
      logicalId: c.id,
      entityKind: 'relationship',
      label: relConflictName(c.ours ?? c.theirs ?? c.base) ?? urnDisplayName(c.id),
      structural: spec.wholeEntityOnly,
      fields: spec.fields.map((name) => fieldDigest(name, base, ours, theirs))
    }
    if (entry.structural) {
      entry.targetChange = describeSide(c.base, c.ours)
      entry.sourceChange = describeSide(c.base, c.theirs)
    }
    out.push(entry)
  }

  return out
}
